import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Megaphone, Plus } from 'lucide-react';
import { AppError } from '@/core/errors/AppError';
import { useSession } from '@/core/auth/useSession';
import { useDesactivarPromotor, usePromotoresLista, } from '@/data/hooks/usePromotoresAdmin';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { Button } from '@/shared/components/Button';
import { Chip } from '@/shared/components/Chip';
import { ConfirmDialog } from '@/shared/components/ConfirmDialog';
import { Pagination } from '@/shared/components/Pagination';
import { SearchInput } from '@/shared/components/SearchInput';
import { Select } from '@/shared/components/Select';
import { Table } from '@/shared/components/Table';
import { useToast } from '@/shared/components/Toast';
import { PromotorFormDialog } from './PromotorFormDialog';
import { PromotorDetalleDialog } from './PromotorDetalleDialog';
const POR_PAGINA = 20;
export function PromotoresPage() {
    const { tieneRol } = useSession();
    const puedeAdmin = tieneRol('ROLE_ADMINISTRAR_PROMOTOR');
    const toast = useToast();
    const [busq, setBusq] = useState('');
    const [estado, setEstado] = useState('activos');
    const [pagina, setPagina] = useState(1);
    const q = useDebounce(busq, 300);
    const { data, isLoading, isError, error, refetch } = usePromotoresLista({
        q,
        activo: estado === 'todos' ? undefined : estado === 'activos',
        pagina,
        porPagina: POR_PAGINA,
    });
    const [formOpen, setFormOpen] = useState(false);
    const [editable, setEditable] = useState(null);
    const [detalleOpen, setDetalleOpen] = useState(false);
    const [seleccionado, setSeleccionado] = useState(null);
    const [desactivando, setDesactivando] = useState(null);
    const desactivar = useDesactivarPromotor();
    const columnas = useMemo(() => [
        {
            key: 'nombre',
            header: 'Promotor',
            cell: (p) => (_jsxs("div", { className: "flex flex-col", children: [_jsxs("span", { className: "font-medium", children: [p.nombre, p.apellido ? ` ${p.apellido}` : ''] }), _jsx("span", { className: "text-xs text-text-tertiary", children: p.email ?? '—' })] })),
        },
        {
            key: 'tipo',
            header: 'Tipo',
            cell: (p) => p.esEmpleado ? _jsx(Chip, { tono: "info", children: "Instructor" }) : _jsx(Chip, { tono: "neutral", children: "Externo" }),
            width: '120px',
        },
        {
            key: 'documento',
            header: 'Documento',
            cell: (p) => p.tipoDocumento || p.nroDocumento
                ? `${p.tipoDocumento ?? ''} ${p.nroDocumento ?? ''}`.trim()
                : '—',
            width: '180px',
        },
        {
            key: 'override',
            header: 'Override % (ef / tj)',
            cell: (p) => {
                const ef = p.pctOverrideEfectivo != null ? `${p.pctOverrideEfectivo}%` : '—';
                const tj = p.pctOverrideTarjeta != null ? `${p.pctOverrideTarjeta}%` : '—';
                return `${ef} / ${tj}`;
            },
            width: '150px',
            align: 'right',
        },
        {
            key: 'telefono',
            header: 'Teléfono',
            cell: (p) => p.telefono ?? '—',
            width: '140px',
        },
        {
            key: 'estado',
            header: 'Estado',
            width: '110px',
            cell: (p) => p.activo ? _jsx(Chip, { tono: "success", children: "Activo" }) : _jsx(Chip, { tono: "neutral", children: "Inactivo" }),
        },
    ], []);
    const abrirNuevo = () => {
        setEditable(null);
        setFormOpen(true);
    };
    const abrirEdicion = (p) => {
        setEditable(p);
        setDetalleOpen(false);
        setFormOpen(true);
    };
    const ejecutarDesactivar = (p) => {
        desactivar.mutate(p.id, {
            onSuccess: () => {
                toast.exito('Promotor desactivado');
                setDesactivando(null);
                setDetalleOpen(false);
            },
            onError: (err) => toast.error(err instanceof AppError ? err.mensaje : 'No pudimos desactivarlo.'),
        });
    };
    return (_jsxs("div", { className: "flex flex-col gap-4", children: [_jsxs("div", { className: "flex flex-wrap items-start justify-between gap-3", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-xl font-bold text-text-primary", children: "Promotores" }), _jsx("p", { className: "text-xs text-text-secondary", children: "Personas o entidades que traen reservas y cobran comisi\u00F3n." })] }), puedeAdmin ? (_jsxs(Button, { onClick: abrirNuevo, children: [_jsx(Plus, { className: "h-4 w-4" }), "Nuevo promotor"] })) : null] }), _jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx(SearchInput, { value: busq, onChange: (v) => {
                            setBusq(v);
                            setPagina(1);
                        }, placeholder: "Buscar por nombre, email o documento\u2026" }), _jsxs(Select, { value: estado, onChange: (e) => {
                            setEstado(e.target.value);
                            setPagina(1);
                        }, className: "w-36", children: [_jsx("option", { value: "activos", children: "Activos" }), _jsx("option", { value: "inactivos", children: "Inactivos" }), _jsx("option", { value: "todos", children: "Todos" })] })] }), isError ? (_jsxs("div", { className: "rounded-lg border border-danger bg-danger-fill px-3 py-2 text-sm text-danger", children: [error instanceof AppError ? error.mensaje : 'Error al cargar.', ' ', _jsx("button", { onClick: () => refetch(), className: "ml-2 underline", children: "Reintentar" })] })) : (_jsxs(_Fragment, { children: [_jsx(Table, { columns: columnas, items: data?.items ?? [], rowKey: (p) => p.id, onRowClick: (p) => {
                            setSeleccionado(p);
                            setDetalleOpen(true);
                        }, cargando: isLoading, vacioTitulo: "Sin promotores", vacioDescripcion: q ? 'Probá con otra búsqueda.' : 'Creá el primer promotor.', vacioAccion: puedeAdmin && !q ? (_jsxs(Button, { onClick: abrirNuevo, children: [_jsx(Megaphone, { className: "h-4 w-4" }), "Crear el primero"] })) : null }), data ? (_jsx(Pagination, { pagina: data.paginacion.pagina, totalPaginas: data.paginacion.totalPaginas, total: data.paginacion.total, porPagina: data.paginacion.porPagina, onCambioPagina: setPagina })) : null] })), _jsx(PromotorFormDialog, { open: formOpen, onOpenChange: setFormOpen, promotor: editable }), _jsx(PromotorDetalleDialog, { open: detalleOpen, onOpenChange: setDetalleOpen, promotor: seleccionado, onEditar: () => seleccionado && abrirEdicion(seleccionado), onDesactivar: () => seleccionado && setDesactivando(seleccionado) }), _jsx(ConfirmDialog, { open: Boolean(desactivando), onOpenChange: (open) => !open && setDesactivando(null), titulo: "Desactivar promotor", descripcion: desactivando
                    ? `${desactivando.nombre}${desactivando.apellido ? ' ' + desactivando.apellido : ''} dejará de aparecer como opción en nuevas reservas.`
                    : undefined, textoConfirmar: "Desactivar", destructivo: true, cargando: desactivar.isPending, onConfirmar: () => desactivando && ejecutarDesactivar(desactivando) })] }));
}
